/**
 * Incremental Server-Sent Events parser (WHATWG "event stream interpretation") for
 * SSE-over-fetch. Handles CRLF/CR/LF line endings, chunk boundaries anywhere (including
 * inside multi-byte UTF-8 sequences), comments, multi-line data, `id` and `retry`.
 */
export interface SseMessage {
  /** Event type; "message" when the stream omits `event:`. */
  event: string;
  data: string;
  /** The last event ID in effect when this message was dispatched. */
  id: string | null;
  retry: number | null;
}

const DEFAULT_EVENT = "message";

export class SseParser {
  private buffer = "";
  private eventType = "";
  private dataLines: string[] = [];
  private retry: number | null = null;
  private pendingCr = false;
  lastEventId: string | null;

  constructor(initialLastEventId: string | null = null) {
    this.lastEventId = initialLastEventId;
  }

  /** Feeds decoded text; returns every message completed by this chunk. */
  feed(chunk: string): SseMessage[] {
    let text = chunk;
    if (this.pendingCr && text.startsWith("\n")) text = text.slice(1);
    this.pendingCr = false;
    this.buffer += text;
    const messages: SseMessage[] = [];
    for (;;) {
      const match = /\r\n|\r|\n/.exec(this.buffer);
      if (!match) break;
      // A trailing lone CR may be the first half of CRLF split across chunks.
      if (match[0] === "\r" && match.index === this.buffer.length - 1) this.pendingCr = true;
      const line = this.buffer.slice(0, match.index);
      this.buffer = this.buffer.slice(match.index + match[0].length);
      const message = this.processLine(line);
      if (message) messages.push(message);
    }
    return messages;
  }

  /** Flushes at end of stream. Per spec, an unterminated final event is discarded. */
  end(): SseMessage[] {
    this.buffer = "";
    this.resetEvent();
    return [];
  }

  private processLine(line: string): SseMessage | null {
    if (line === "") return this.dispatch();
    if (line.startsWith(":")) return null;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    this.processField(field, value);
    return null;
  }

  private processField(field: string, value: string): void {
    switch (field) {
      case "event":
        this.eventType = value;
        return;
      case "data":
        this.dataLines.push(value);
        return;
      case "id":
        if (!value.includes("\u0000")) this.lastEventId = value;
        return;
      case "retry":
        if (/^\d+$/.test(value)) this.retry = Number(value);
        return;
      default:
        return;
    }
  }

  private dispatch(): SseMessage | null {
    if (this.dataLines.length === 0) {
      this.resetEvent();
      return null;
    }
    const message: SseMessage = {
      event: this.eventType || DEFAULT_EVENT,
      data: this.dataLines.join("\n"),
      id: this.lastEventId,
      retry: this.retry,
    };
    this.resetEvent();
    return message;
  }

  private resetEvent(): void {
    this.eventType = "";
    this.dataLines = [];
    this.retry = null;
  }
}

export interface ParseSseOptions {
  /** Resume point sent as `Last-Event-ID` on reconnect. */
  lastEventId?: string | null;
  /** Called whenever the stream sets a new event id. */
  onEventId?: (id: string) => void;
  signal?: AbortSignal;
}

/** Async-iterates SSE messages from a fetch `Response.body`. */
export async function* parseSSE(
  stream: ReadableStream<Uint8Array>,
  options: ParseSseOptions = {},
): AsyncGenerator<SseMessage, void, undefined> {
  const parser = new SseParser(options.lastEventId ?? null);
  const decoder = new TextDecoder("utf-8");
  const reader = stream.getReader();
  let previousId = parser.lastEventId;
  try {
    for (;;) {
      if (options.signal?.aborted) return;
      const { value, done } = await reader.read();
      const messages = done ? [...parser.feed(decoder.decode()), ...parser.end()] : parser.feed(decoder.decode(value, { stream: true }));
      for (const message of messages) {
        if (message.id !== null && message.id !== previousId) {
          previousId = message.id;
          options.onEventId?.(message.id);
        }
        yield message;
      }
      if (done) return;
    }
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
